import { AlertOptions, useAppAlert } from '@/app/AppAlertProvider.tsx'
import { MultipleErrorsSchema } from '@/app/common/types_and_interfaces'
import { useWizardContext } from '@/app/pages/family_application/income_verification/IncomeVerificationWizardProvider.tsx'
import { submitDocument } from '@/app/pages/family_application/residency_verification/hooks/useUploadFile.tsx'
import { Button } from '@/common/components/Button'
import { CircleLoader } from '@/common/components/CircleLoader.tsx'
import { CheckMarkIcon } from '@/common/icons/arrows.tsx'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { RefreshFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { AppDispatch, RootState } from '@/storage/store.ts'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

export const IdentityDocumentReview = () => {
  const [isLoading, setIsLoading] = useState(false)
  const { t: translate } = useTranslation()
  const programId = useSelector(selectProgramID)
  const savedImage = useSelector(
    (state: RootState) => state.FamilyApplication.data
  )
  const { formData } = useWizardContext()
  const { fireAlert } = useAppAlert()

  const navigateTo = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  const handleSubmitError = (errorMessage: string) => {
    setIsLoading(false)
    const alertConfig: AlertOptions = {
      variant: 'error',
      message:
        errorMessage ||
        translate('an_error_occurred_whil_processing_the_document'),
      denyBtn: true,
      denyBtnText: translate('retry'),
    }

    fireAlert(alertConfig).then(({ denied }) => {
      if (denied) {
        navigateTo(-1)
      }
    })
  }

  const handleSubmitSuccess = async () => {
    setIsLoading(false)


    dispatch(RefreshFamilyApplication(programId))
    navigateTo(
      AppRoutePaths.family_application.identity_verification
        .success_manual_verification
    )
  }

  const onSubmit = async () => {
    if (!formData.DateOfBirthFile) {
      handleSubmitError(translate('no_document_file_found'))
      return
    }

    setIsLoading(true)
    const form = new FormData()
    form.append('file', formData.DateOfBirthFile)
    form.append('type', String(1))
    form.append('variation', String(2))
    form.append('program_id', String(programId))

    const response = await submitDocument(form)
    const multipleErrors = MultipleErrorsSchema.safeParse(
      response.error?.details?.errorsMessage
    ) 

    if (response.error) {
      setIsLoading(false)
      const alertConfig: AlertOptions = {
        variant: 'error',
        titleText:
          response.error.message ??
          translate('an_error_occurred_whil_processing_the_document'),
        title: true,
        denyBtn: true,
        denyBtnText: translate('retry'),
        messageComponent: (
          <ul className="text-sm space-y-2">
            {multipleErrors.success &&
              multipleErrors.data.map((item) => (
                <li className="list-disc list-inside text-left" key={item}>
                  {item}
                </li>
              ))}
          </ul>
        ),
      }

      fireAlert(alertConfig).then(({ denied }) => {
        if (denied) {
          navigateTo(-1)
        }
      })
    } else {
      await handleSubmitSuccess()
    }
  }

  return (
    <>
      <div className="text-3xl text-center font-medium text-gray-900 mb-10 sm:mb-5">
        {translate('photo_review')}
      </div>

      <div className="flex justify-center">
        {savedImage?.residencyDocument && (
          <img
            src={savedImage?.residencyDocument}
            className="shadow-md"
            alt={translate('submitted_document_preview')}
          />
        )}
      </div>
      <div className="mt-6">
        <div className="flex justify-center text-success text-sm items-center">
          <CheckMarkIcon className="mr-2" /> {translate('document_captured')}
        </div>
      </div>
      <div className="mt-6">
        <div className="flex justify-center">
          <div className="text-xs">{translate('is_the_photo_clear')}</div>
        </div>
      </div>
      <div className="flex justify-center mt-6">
        <button
          className="text-xs font-medium link text-primary"
          onClick={() => navigateTo(-1)}
        >
          {translate('retake_photo')}
        </button>
      </div>
      <div className="text-center h-full flex">
        <div className="flex justify-end my-5 w-full flex-col">
          <Button
            onClick={onSubmit}
            className="rounded h-9"
            disabled={isLoading}
          >
            {isLoading && <CircleLoader size="sm" />}
            <span>
              {isLoading
                ? ` ${translate('processing')}...`
                : translate('continue')}
            </span>
          </Button>
        </div>
      </div>
    </>
  )
}
